import type { AvatarGroupProps } from '@atlaskit/avatar-group/avatar-group';
import type { GlobalShortcutsItem } from '@atlassian/global-shortcuts/types';

export type CrossJoinSourceProduct =
	| 'confluence'
	| 'jira-software'
	| 'jira-product-discovery'
	| 'jira-core'
	| 'jira-servicedesk'
	| 'jira-customer-service';

export type CrossJoinTargetProduct =
	| 'jira-software'
	| 'jira-product-discovery'
	| 'confluence'
	| 'loom';

export type CrossJoinIntegration =
	| 'crossJoinNudgesV3'
	| 'crossJoinNudgesConfluenceToJpd'
	| 'crossJoinNudgesJiraToConfluence'
	| 'crossJoinNudgesJiraToJpd'
	| 'crossJoinNudgesConfluenceToJpdMsr'
	| 'crossJoinNudgesConfluenceToJiraMsr'
	| 'crossJoinNudgesConfluenceToLoom'
	| 'invitedAppShortcuts';

/**
 * @deprecated Use CrossJoinIntegration instead. This was a typo and will be removed in a future version.
 */
export type CrossJoinIntegraton = CrossJoinIntegration;

export type Collaborator = {
	avatar: string;
	id: string;
	name: string;
};

export type Collaborators = Collaborator[];

// manage the allowed apps and their keys
export const INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS = [
	'jira',
	'confluence',
	'jira-servicedesk',
	'loom',
] as const;
export type InvitedAppShortcutAllowedTargetAppKey =
	(typeof INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS)[number];
export const INVITED_APP_SHORTCUTS_ALLOWED_SOURCE_APPS = ['confluence', 'jira', 'home'] as const;
export type InvitedAppShortcutAllowedSourceAppKey =
	(typeof INVITED_APP_SHORTCUTS_ALLOWED_SOURCE_APPS)[number];
export const INVITED_APP_SHORTCUTS_ALLOWED_APS_KEYS = [
	'JIRA_SOFTWARE',
	'CONFLUENCE',
	'JIRA_SERVICE_DESK',
	'LOOM',
] as const;
export type InvitedAppShortcutAllowedApsKey =
	(typeof INVITED_APP_SHORTCUTS_ALLOWED_APS_KEYS)[number];

/**
 * Lozenge variant for an Invited App Shortcut, parsed out of the GRS
 * recommendation `entityId` by `parseInvitedAppShortcutEntityId`.
 *
 * `none` is the safe fallback and means "render the shortcut, but no lozenge".
 * It is what a plain product `entityId` (e.g. `jira`) resolves to, and what an
 * unrecognised variant suffix degrades to, so an unknown future variant from
 * GRS can never make a shortcut disappear.
 */
export const INVITED_APP_SHORTCUT_VARIANTS = ['animated', 'static', 'none'] as const;
export type InvitedAppShortcutVariant = (typeof INVITED_APP_SHORTCUT_VARIANTS)[number];

/**
 * Lozenge state a cache refresh can ask GRS to write.
 *
 * MUST stay in sync with the GRS backend that parses these values (PUMA-2362) —
 * the variant vocabulary is a two-way contract now, not just an inbound one.
 *
 * Derived with `Exclude` so `none` is unrepresentable on the wire: the no-lozenge
 * case is entity omission decided by eligibility, not something a refresh can ask
 * for. Deriving from the same union also stops the two vocabularies drifting.
 */
export type InvitedAppShortcutLozengeState = Exclude<InvitedAppShortcutVariant, 'none'>;

/**
 * A recommended target app together with the lozenge variant parsed from its
 * GRS `entityId`.
 */
export type InvitedAppShortcutRecommendedApp = {
	product: InvitedAppShortcutAllowedTargetAppKey;
	variant: InvitedAppShortcutVariant;
};

/**
 * Accepted element type for `recommendedApps`.
 *
 * Now a straight alias for `InvitedAppShortcutRecommendedApp`: the bare
 * `InvitedAppShortcutAllowedTargetAppKey` member that existed for backward
 * compatibility during the PUMA-2247 rollout was removed in PR 4, once Jira and
 * Confluence both passed the object form.
 *
 * Kept as a distinct name so call sites and `toRecommendedProducts` do not all
 * have to churn again; it can be collapsed into
 * `InvitedAppShortcutRecommendedApp` opportunistically.
 */
export type InvitedAppShortcutRecommendedAppInput = InvitedAppShortcutRecommendedApp;

export type ShortcutItemsConfig = Partial<
	Record<InvitedAppShortcutAllowedTargetAppKey, GlobalShortcutsItem>
>;

export type InvitedAppShortcutCollaborator = Pick<
	AvatarGroupProps['data'][number],
	'name' | 'src' | 'key'
>;
